import { useEffect, useMemo } from 'react';

import { APP_PRODUCT_TYPE, FEATURE_FLAGS_MULTI_ACCOUNTS } from 'appConstants';
import { useMultiAccounts } from 'contexts/MultiAccounts';
import SingleSelectField from 'fields/SingleSelectField';

import type { IMultiAccountField } from './types';

export default function MultiAccountField({
  name,
  label,
  placeholder,
  onChange,
  onCloseIconClick,
  customerReference,
  productType = APP_PRODUCT_TYPE.FERTILISER,
  disabled,
  required,
}: IMultiAccountField) {
  const {
    state: { loadedProductType, multiAccounts, selectedCustomerReference, loading, error },
    actions: { loadMultiAccounts, resetError },
  } = useMultiAccounts();

  useEffect(() => {
    if (FEATURE_FLAGS_MULTI_ACCOUNTS) {
      if (customerReference !== selectedCustomerReference && error) {
        resetError();
      }

      if (
        !loading &&
        !error &&
        customerReference &&
        (customerReference !== selectedCustomerReference || loadedProductType !== productType)
      ) {
        loadMultiAccounts(customerReference, productType);
      }
    }
  }, [
    loading,
    error,
    resetError,
    customerReference,
    selectedCustomerReference,
    loadMultiAccounts,
    loadedProductType,
    productType,
  ]);

  const getOptions = useMemo(
    () =>
      multiAccounts.map((option) => ({
        value: option.accountDescription,
        label: option.accountDescription,
      })),
    [multiAccounts],
  );

  if (!FEATURE_FLAGS_MULTI_ACCOUNTS) return null;
  if (multiAccounts.length === 0) return null;

  return (
    <SingleSelectField
      required={required}
      disabled={disabled}
      data-testid="multi-account-field"
      name={name}
      label={label}
      placeholder={placeholder}
      options={getOptions}
      showClearIcon={true}
      onChange={onChange}
      onCloseIconClick={onCloseIconClick}
      loading={loading}
    />
  );
}
